import React, { FC, useMemo } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Description, Header, InternalProjectDetailsSection, StartedAt, Title } from '../Layout';
import { AppI18n } from '../../../../i18n';
import { ProjectProps } from '../Project';
import { ViolatedFields } from '../../../../utils/getPortfolioModerationDetails';
import { usePortfolioModalContext } from '../../PortfolioModalContext';
import { getPortfolioDateFormatter } from '../../../../utils';
import { InternalProjectDetails } from '../../../shared/internal-project';
import { ModerationBadge } from '../../ModerationBadge';
import { ContactSellerButton } from './ProjectHeader.ContactButton';

interface ProjectHeaderProps
  extends Partial<Pick<ProjectProps, 'title' | 'description' | 'projectStartedAt' | 'id' | 'source'>> {
  internalProjectDetails?: InternalProjectDetails;
  isViolated: boolean;
  violatedFields: ViolatedFields;
}

export const ProjectHeader: FC<ProjectHeaderProps> = ({
  title,
  description,
  projectStartedAt,
  internalProjectDetails,
  id: projectId,
  source,
  isViolated = false,
  violatedFields,
}) => {
  const { username } = usePortfolioModalContext();

  const dateFormatter = useMemo(() => getPortfolioDateFormatter(), []);
  const { isDisabledPortfolioProjectModeration } = usePortfolioModalContext();
  const isViolatedAndNotDisabledByRollout = isViolated && !isDisabledPortfolioProjectModeration;

  return (
    <Container>
      <Header>
        <Stack gap="2" direction="column">
          {projectStartedAt && (
            <StartedAt>
              <AppI18n
                k="project_modal.started_at"
                params={{
                  date: dateFormatter.format(projectStartedAt * 1000),
                }}
              ></AppI18n>
            </StartedAt>
          )}
          <Stack gap="2">
            {title && <Title>{title}</Title>}
            {isViolatedAndNotDisabledByRollout && violatedFields?.isHeaderViolated && (
              <ModerationBadge marginTop="2.5" />
            )}
          </Stack>
        </Stack>
        <Container>
          {projectId && source && !isViolated && (
            <ContactSellerButton sellerUserName={username} projectId={projectId} source={source} />
          )}
        </Container>
      </Header>
      {internalProjectDetails && <InternalProjectDetailsSection internalProjectDetails={internalProjectDetails} />}
      {description && (
        <Description>
          {description}
          {isViolatedAndNotDisabledByRollout && violatedFields?.isDescriptionViolated && (
            <ModerationBadge display="inlineFlex" marginLeft="2" />
          )}
        </Description>
      )}
    </Container>
  );
};
